import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { Result } from '@src/services/common/Result';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

export interface RenameClipServiceDeps {
  datoClientStore: DatoClientStore;
}

/**
 * PLAT-11213 DS10 — write a clip's title, and nothing else.
 *
 * Not `videoCreator.renameVideo`: that throws when `videoCreator.currentVideo`
 * is unset — the state Content Studio is in, and the reason `useSaveClipsAsReel`
 * saves through `saveLightweightReelFromStory` rather than `saveProducedClips` —
 * and it saves the current and parent videos as a side effect, which a title
 * edit has no business triggering.
 *
 * Where the new title lands in memory is the caller's (the story's
 * `otherVideos`, the clip-preview cache); this owns the record and the failure.
 */
export class RenameClipService {
  private readonly _datoClientStore: DatoClientStore;
  private readonly _logger = LogManager.getLogger('service:RenameClipService');

  constructor(deps: RenameClipServiceDeps) {
    this._datoClientStore = deps.datoClientStore;
  }

  /**
   * Renames the clip record. A blank title is rejected rather than written: a
   * clip list has nothing to render for a nameless clip (DS8).
   */
  async rename(clipId: string, title: string): Promise<Result<void>> {
    const trimmed = title.trim();
    if (!clipId) return err('VALIDATION', 'Cannot rename a clip with no id');
    if (!trimmed) return err('VALIDATION', 'A clip cannot be left nameless');

    try {
      await this._datoClientStore.videoRepository.updateVideo({
        id: clipId,
        title: trimmed,
      });
      return ok(undefined);
    } catch (error) {
      this._logger.error('Failed to rename the clip', {
        clipId,
        ...extractErrorInfo(error),
      });
      return err('EXTERNAL', 'Could not save the new name', error);
    }
  }
}
